import { Injectable } from '@angular/core';
import { Queue } from '@app/core/interfaces/c5/acd/queue';
import { QueueWithUser } from '@app/core/interfaces/c5/acd/queue-with-user';
import { RouteConfig } from '@app/core/interfaces/c5/acd/route-config';
import { Announcement } from '@app/core/interfaces/c5/announcement';
import { RoutingGroup } from '@app/core/interfaces/c5/routing-group';
import { SiteLocal } from '@app/core/interfaces/site-local';
import { AcdAnnouncementRepository } from '@app/core/repositories/acd-announcement.repository';
import { AcdRepository } from '@app/core/repositories/acd.repository';
import { RoutingGroupsRepository } from '@app/core/repositories/routing-groups.repository';
import { AcdCallCenterUtilsService } from '@app/shared/acd-cc/utils/acd-call-center-utils-service';
import { AcdUtilsService } from '@app/shared/acd-cc/utils/acd-utils.service';
import { CachedHelperService } from '@app/shared/services/cached-helper.service';
import { EnterpriseContextService } from '@app/shared/services/context/enterprise-context.service';
import { Cached, cachedGroup } from '@app/utils/cached';
import { toMap } from '@app/utils/common';
import { isPhoneNumberBillable } from '@app/utils/is-phone-number-billable';
import { wrapError } from '@app/utils/rxjs';
import { StringMap } from '@app/utils/string-map';
import { AcdContext } from '@business/acd/models/acd-context';
import { AcdViewModel } from '@business/acd/models/acd-view-model';
import { AcdsViewModel } from '@business/acd/models/acds-view-model';
import { forkJoin, Observable, of } from 'rxjs';
import { delayWhen, first, map } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AcdService {
  private _announcements: Cached<Announcement[]> | null = null;

  get announcements(): Cached<Announcement[]> {
    this._announcements ??= cachedGroup(
      [this.queue],
      ([{ queue }]) => {
        return this.acdAnnouncementRepository.list(queue.id, true);
      },
      {
        id: `acd:announcements`,
      }
    );

    return this._announcements;
  }

  private _queue: Cached<QueueWithUser> | null = null;

  get queue(): Cached<QueueWithUser> {
    this._queue ??= this.cachedHelperService.routeAwareCached({
      dataProducer: ([, queueId]) => this.acdRepository.get(Number(queueId)),
      id: ([enterpriseId, queueId]) => `enterprise:${enterpriseId}:acd:queue:${queueId}`,
      routeParameters: ['enterpriseId', 'queueId'],
    });

    return this._queue;
  }

  private _queueContext: Cached<AcdContext> | null = null;

  get queueContext(): Cached<AcdContext> {
    this._queueContext ??= cachedGroup(
      [this.queue, this.queues, this.routingGroups],
      ([{ queue, user }, queues, routingGroups]) => {
        return this.acdCallCenterUtilsService.getPcrState(user).pipe(
          first(),
          map((pcrState): AcdContext => {
            return {
              initialized: true,
              pcrAvailable: pcrState.available,
              pcrEnabled: pcrState.enabled,
              queue: this.acdUtilsService.enhanceQueue(queue, user),
              queues: queues.map((queue) => ({
                key: queue.name,
                label: queue.name,
                value: queue.id,
              })),
              routingGroups,
              user,
            };
          })
        );
      },
      {
        id: 'acd:queue-context',
      }
    );

    return this._queueContext;
  }

  private _queues: Cached<Queue[]> | null = null;

  private get queues(): Cached<Queue[]> {
    this._queues ??= this.cachedHelperService.routeAwareCached({
      dataProducer: () => {
        return this.acdRepository.list();
      },
      id: ([enterpriseId]) => `enterprise:${enterpriseId}:acd:queues`,
      routeParameters: ['enterpriseId'],
    });

    return this._queues;
  }

  private _queuesViewModel: Cached<AcdsViewModel> | null = null;

  get queuesViewModel(): Cached<AcdsViewModel> {
    this._queuesViewModel ??= cachedGroup(
      [this.queues, this.enterpriseContextService.enterprise, this.enterpriseContextService.timeTables],
      ([queues, { defaultSite, localSites: sites }, timeTables]): Observable<AcdsViewModel> => {
        const siteMap = toMap(sites, 'id');

        return of({
          acds: queues.map((entry) => this.mapEntry(entry, siteMap)),
          defaultSiteId: defaultSite?.id || sites[0].id,
          hasTimeTables: timeTables.length > 0,
          isMultiSite: sites.length > 1,
          siteMap,
          sites,
        });
      },
      {
        id: 'enterprise:acd:queues-view-model',
      }
    );

    return this._queuesViewModel;
  }

  private _routingGroups: Cached<RoutingGroup[]> | null = null;

  private get routingGroups(): Cached<RoutingGroup[]> {
    this._routingGroups ??= cachedGroup(
      [this._queue],
      ([{ queue }]) => this.routingsGroupRepository.listForSite(queue.siteId),
      {
        id: 'acd:queue-context:routing-groups',
      }
    );

    return this._routingGroups;
  }

  private _routeConfigs: Cached<RouteConfig[]> | null = null;

  get routeConfigs(): Cached<RouteConfig[]> {
    this._routeConfigs ??= cachedGroup(
      [this.queueContext],
      ([{ queue }]) => {
        return this.acdRepository.listRouteConfigs(queue.id);
      },
      {
        id: `acd:route-configs`,
      }
    );

    return this._routeConfigs;
  }

  constructor(
    private acdAnnouncementRepository: AcdAnnouncementRepository,
    private acdCallCenterUtilsService: AcdCallCenterUtilsService,
    private acdRepository: AcdRepository,
    private acdUtilsService: AcdUtilsService,
    private cachedHelperService: CachedHelperService,
    private enterpriseContextService: EnterpriseContextService,
    private routingsGroupRepository: RoutingGroupsRepository
  ) {}

  add(queue: Partial<Queue>): Observable<void> {
    return this.acdRepository.add(queue).pipe(
      delayWhen(() => forkJoin([this.queues.reload(), this._queue?.reload() ?? of(undefined)])),
      wrapError('acd-service:add')
    );
  }

  delete(id: number): Observable<void> {
    return this.acdRepository.delete(id).pipe(
      delayWhen(() => this._queues.reload()),
      wrapError('acd-service:delete')
    );
  }

  setPcrState({ available, enabled }: PcrStateUpdate): void {
    this.queueContext.data$.pipe(first()).subscribe((context) => {
      this.queueContext.setData({
        ...context,
        pcrAvailable: available ?? context.pcrAvailable,
        pcrEnabled: enabled ?? context.pcrEnabled,
      });
    });
  }

  update(queue: Queue): Observable<void> {
    return this.acdRepository.update(queue).pipe(
      delayWhen(() => forkJoin([this.queue.reload(), this._queues?.reload() ?? of(undefined)])),
      wrapError('acd-service:update')
    );
  }

  private mapEntry(queue: Queue, siteMap: StringMap<SiteLocal>): AcdViewModel {
    return {
      _original: queue,
      loggedInAgentsCount: queue.loggedInAgentsCount,
      agents: queue.agents,
      description: queue.description,
      distribution: queue.distribution,
      hasInternal: queue.phoneNumbers?.some((phoneNumber) => phoneNumber.type === 'INTERNAL'),
      hasNumbers: queue.phoneNumbers?.length > 0,
      hasBillableNumber: queue.phoneNumbers.some((pn) => isPhoneNumberBillable(pn)),
      id: queue.id,
      maxAgents: queue.agents.length,
      name: queue.name,
      phoneNumbers: queue.phoneNumbers,
      site: siteMap[queue.siteId]?.name,
      features: queue.features,
    };
  }
}

interface PcrStateUpdate {
  available?: boolean;
  enabled?: boolean;
}
